/**
 * RoomLandingGrid — the "My Rooms" landing grid (sibling of NetworkDashboardGrid).
 *
 * A FLAT grid of RoomLandingTile (3-col, 9/page) for the rooms the caller belongs to,
 * across all networks — for room owners/supervisors/viewers who may not be network
 * members. Running rooms float to #1 (reactive, same as the networks grid). A pinned
 * fleet footer summarises the shown rooms (count · runs breakdown · consumption) and
 * carries the pagination, matching the Networks tab's footer exactly.
 *
 * Unlike NetworkDashboardGrid this is single-level (no inline expansion) — a room IS
 * the leaf. Each tile's primary CTA opens that room's executions.
 */
import React, { useMemo, useState, useCallback, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, ChevronLeft, ChevronRight, Activity, Server, Check, X, ExternalLink } from 'lucide-react';
import { ConsumptionMeter } from '@stepflow/ui-shared/components/dashboard/vitalWidgets.jsx';
import { listNetworkExecutions } from '@stepflow/ui-shared/services/networkExecutionsApi.ts';
import { listPoolMembers } from '@stepflow/ui-shared/services/poolMembersApi.js';
import RoomLandingTile from './RoomLandingTile.jsx';
import RoomExecutionRow from './RoomExecutionRow.jsx';

// Executions per page in the inline overview — server-side paginated (10/page,
// most-recent-first). Production rooms can have huge histories, so this never
// over-fetches: each page is one PoolExecutionsIndex query. "Open full history"
// jumps to the room's own Executions tab for the deep surface.
const EXEC_PAGE_SIZE = 10;

const PAGE_SIZE = 9;

export default function RoomLandingGrid({
  rooms,             // merged room-vitals rows (from getMyRoomsVitals)
  loading,
  error,
  query = '',
  onOpenRoom,        // (networkId, poolId) => void — open the room
  onOpenExecutions,  // (networkId, poolId) => void — deep-link to the room's full Executions tab
  emptyHint,
}) {
  const [page, setPage] = useState(0);
  const [footerUnit, setFooterUnit] = useState('usd');
  // Inline executions overview: which room is expanded + its server-paged rows.
  // Mirrors NetworkDashboardGrid's room-expansion pattern, but the leaf is a
  // cursor-paginated execution list (10/page, most-recent-first).
  const [expandedKey, setExpandedKey] = useState(null);
  // key -> { rows, usersBySub, loading, error,
  //          nextCursor, cursorStack: [], pageIndex }
  //   cursorStack — the cursors used to REACH prior pages, for the "Prev" button.
  //   DynamoDB queries are forward-only, so back-nav is a client-side stack.
  const [execsByKey, setExecsByKey] = useState({});
  const loadingRef = useRef(new Set());

  // Fetch ONE page of a room's executions (server-side, most-recent-first). The
  // roster (sub→name) is fetched only when we don't already have it for this room
  // and merged in — so paging back and forth doesn't re-fetch members every time.
  const loadExecsPage = useCallback(async (room, { cursor = null, direction = 'first' } = {}) => {
    const key = `${room.networkId}:${room.poolId}`;
    if (loadingRef.current.has(key)) return;
    loadingRef.current.add(key);
    setExecsByKey((m) => ({ ...m, [key]: { ...(m[key] || {}), loading: true, error: null } }));
    try {
      const haveRoster = execsByKey[key]?.usersBySub;
      const [execRes, memberRes] = await Promise.all([
        listNetworkExecutions(room.networkId, {
          poolId: room.poolId, limit: EXEC_PAGE_SIZE, cursor: cursor || undefined,
        }),
        haveRoster ? Promise.resolve(null) : listPoolMembers(room.networkId, room.poolId).catch(() => null),
      ]);
      const executions = execRes?.executions || [];
      let usersBySub = haveRoster || {};
      if (!haveRoster) {
        // sub → best display string. ``userName`` is the person's workspace name
        // (JWT claims); ``email`` is the fallback. ``displayName`` on a membership
        // is the ROOM name, not the user — deliberately not used here.
        usersBySub = {};
        for (const mem of (memberRes?.members || [])) {
          const sub = String(mem?.userSub || '').trim();
          if (!sub) continue;
          const name = String(mem?.userName || '').trim();
          const email = String(mem?.email || '').trim();
          if (name || email) usersBySub[sub] = name || email;
        }
      }
      setExecsByKey((m) => {
        const prev = m[key] || {};
        const stack = Array.isArray(prev.cursorStack) ? prev.cursorStack : [];
        // Invariant: cursorStack[i] = the cursor used to fetch page i+1 (page 1 = null),
        // so pageIndex is simply cursorStack.length.
        let cursorStack;
        if (direction === 'next') cursorStack = [...stack, cursor];      // cursor = prev.nextCursor
        else if (direction === 'prev') cursorStack = stack.slice(0, -1); // drop current page
        else cursorStack = [null];                                       // 'first'
        return {
          ...m,
          [key]: {
            rows: executions,
            usersBySub,
            nextCursor: execRes?.nextCursor || null,
            cursorStack,
            pageIndex: cursorStack.length,
            loading: false,
            error: null,
          },
        };
      });
    } catch (e) {
      setExecsByKey((m) => ({
        ...m, [key]: { ...(m[key] || {}), loading: false, error: e?.message || 'Failed to load' },
      }));
    } finally {
      loadingRef.current.delete(key);
    }
  }, [execsByKey]);

  const nextExecPage = useCallback((room) => {
    const key = `${room.networkId}:${room.poolId}`;
    const ex = execsByKey[key];
    if (!ex?.nextCursor) return;
    loadExecsPage(room, { cursor: ex.nextCursor, direction: 'next' });
  }, [execsByKey, loadExecsPage]);

  const prevExecPage = useCallback((room) => {
    const key = `${room.networkId}:${room.poolId}`;
    const ex = execsByKey[key];
    if (!ex || (ex.pageIndex || 1) <= 1) return;
    // The cursor that reaches the PREVIOUS page is the second-to-last on the stack
    // (last entry reached the current page). null = the first page.
    const stack = ex.cursorStack || [];
    const prevCursor = stack.length >= 2 ? stack[stack.length - 2] : null;
    loadExecsPage(room, { cursor: prevCursor, direction: 'prev' });
  }, [execsByKey, loadExecsPage]);

  const toggleExecs = useCallback((room) => {
    const key = `${room.networkId}:${room.poolId}`;
    setExpandedKey((cur) => {
      const next = cur === key ? null : key;
      // On (re-)expand always reload the FIRST page — a re-open should show the
      // latest runs, not resume a stale mid-history page from a prior peek. The
      // roster (usersBySub) is cached inside loadExecsPage so this is one cheap query.
      if (next) loadExecsPage(room, { direction: 'first' });
      return next;
    });
  }, [execsByKey, loadExecsPage]);

  // Search + running-first order. A live room (isActive) floats to #1; the rest keep
  // their incoming (recency) order — the list arrives lastActivityAt-sorted.
  const items = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    const match = (r) => !q
      || String(r.name || '').toLowerCase().includes(q)
      || String(r.poolId || '').toLowerCase().includes(q)
      || String(r.networkId || '').toLowerCase().includes(q);
    const matched = (rooms || []).filter(match);
    const rank = (r) => (r.isActive ? 0 : 1);
    return [...matched].sort((a, b) => rank(a) - rank(b));
  }, [rooms, query]);

  const total = items.length;
  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
  const safePage = Math.min(page, maxPage);
  const pageItems = items.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  // Cumulative "fleet" totals across the shown rooms (all pages).
  const fleet = useMemo(() => {
    const t = { count: items.length, active: 0, runsTotal: 0, runsSucceeded: 0, usd: 0, points: 0 };
    for (const r of items) {
      if (r.isActive) t.active += 1;
      t.runsTotal += Number(r.runsTotal || 0);
      t.runsSucceeded += Number(r.runsSucceeded || 0);
      t.usd += Number(r.consumedUsd || 0);
      t.points += Number(r.consumedPoints || 0);
    }
    t.runsFailed = Math.max(0, t.runsTotal - t.runsSucceeded);
    t.successPct = t.runsTotal > 0 ? Math.round((100 * t.runsSucceeded) / t.runsTotal) : null;
    return t;
  }, [items]);

  const goPrev = useCallback(() => setPage((p) => Math.max(0, p - 1)), []);
  const goNext = useCallback(() => setPage((p) => Math.min(maxPage, p + 1)), [maxPage]);

  if (loading && (!rooms || rooms.length === 0)) {
    return (
      <div className="flex items-center justify-center gap-2 py-12 text-xs text-gray-400" data-testid="room-landing-loading">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading rooms…
      </div>
    );
  }
  if (error && (!rooms || rooms.length === 0)) {
    return <p className="py-8 text-center text-xs text-red-500">{error}</p>;
  }
  if (total === 0) {
    if (query) return <p className="py-8 text-center text-xs text-gray-400">No rooms match “{query}”.</p>;
    return (
      <p className="py-8 text-center text-xs text-gray-400" data-testid="room-landing-empty">
        {emptyHint || 'You’re not a member of any rooms yet.'}
      </p>
    );
  }

  return (
    // Fixed-height flex column: tiles scroll, the footer stays pinned (matches the
    // Networks tab so switching tabs doesn't jump the footer bar).
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
          {pageItems.map((r) => {
            const key = `${r.networkId}:${r.poolId}`;
            const expanded = expandedKey === key;
            const ex = execsByKey[key];
            return (
              // An expanded room spans the full row so its execution overview has room
              // to breathe (same treatment as NetworkDashboardGrid's room expansion).
              <div key={key} className={expanded ? 'sm:col-span-2 md:col-span-3' : ''}>
                <RoomLandingTile
                  room={r}
                  expanded={expanded}
                  onOpen={onOpenRoom}
                  onToggleExecutions={toggleExecs}
                />
                <AnimatePresence initial={false}>
                  {expanded ? (
                    <motion.div
                      layout
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                      data-testid={`room-execs-expansion-${r.networkId}-${r.poolId}`}
                    >
                      <div className="mt-2 overflow-hidden rounded-lg border border-gray-100 bg-white">
                        {ex?.loading && !ex?.rows ? (
                          <div className="flex items-center gap-2 px-3 py-3 text-[11px] text-gray-400">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> Loading executions…
                          </div>
                        ) : ex?.error ? (
                          <p className="px-3 py-3 text-center text-[11px] text-red-500">{ex.error}</p>
                        ) : (ex?.rows || []).length === 0 ? (
                          <p className="px-3 py-3 text-center text-[11px] text-gray-400">No executions in this room yet.</p>
                        ) : (
                          <>
                            <div className="overflow-x-auto">
                              <table className="w-full table-fixed border-collapse text-left">
                                {/* Proportional widths: the small metadata columns stay
                                    tight so Prompt and — most of all — Task done get the
                                    space (they hold the real content). table-fixed makes
                                    truncate/line-clamp size against these widths, keeping
                                    the row height compact. */}
                                <colgroup>
                                  <col className="w-[8%]" />{/* Status */}
                                  <col className="w-[7%]" />{/* Trigger */}
                                  <col className="w-[12%]" />{/* User */}
                                  <col className="w-[27%]" />{/* Prompt */}
                                  <col className="w-[34%]" />{/* Task done — widest */}
                                  <col className="w-[7%]" />{/* Started */}
                                  <col className="w-[5%]" />{/* Duration */}
                                </colgroup>
                                <thead className="border-b border-gray-100 bg-gray-50/80 text-[9px] font-semibold uppercase tracking-wide text-gray-400">
                                  <tr>
                                    <th className="px-2 py-1.5 font-semibold">Status</th>
                                    <th className="px-2 py-1.5 font-semibold">Trigger</th>
                                    <th className="px-2 py-1.5 font-semibold">User</th>
                                    <th className="px-2 py-1.5 font-semibold">Prompt</th>
                                    <th className="px-2 py-1.5 font-semibold">Task done</th>
                                    <th className="px-2 py-1.5 text-right font-semibold">Started</th>
                                    <th className="px-2 py-1.5 text-right font-semibold">Duration</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                  {ex.rows.map((row) => (
                                    <RoomExecutionRow
                                      key={row.taskId}
                                      row={row}
                                      resolveUser={(sub) => ex.usersBySub?.[sub] || null}
                                    />
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            {/* Footer: server-side page nav (10/page, most-recent-first)
                                + a jump to the room's full Executions tab. */}
                            <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50/60 px-2.5 py-1.5 text-[10px] text-gray-500">
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  disabled={(ex.pageIndex || 1) <= 1 || ex.loading}
                                  onClick={() => prevExecPage(r)}
                                  data-testid={`room-execs-prev-${r.networkId}-${r.poolId}`}
                                  className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 hover:bg-gray-100 disabled:opacity-40"
                                >
                                  <ChevronLeft className="h-3 w-3" aria-hidden /> Prev
                                </button>
                                <span className="tabular-nums">
                                  Page {ex.pageIndex || 1}
                                  {ex.loading ? '…' : ''}
                                </span>
                                <button
                                  type="button"
                                  disabled={!ex.nextCursor || ex.loading}
                                  onClick={() => nextExecPage(r)}
                                  data-testid={`room-execs-next-${r.networkId}-${r.poolId}`}
                                  className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 hover:bg-gray-100 disabled:opacity-40"
                                >
                                  Next <ChevronRight className="h-3 w-3" aria-hidden />
                                </button>
                              </div>
                              <button
                                type="button"
                                onClick={() => onOpenExecutions?.(r.networkId, r.poolId)}
                                data-testid={`room-execs-open-${r.networkId}-${r.poolId}`}
                                className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium text-blue-600 hover:bg-blue-50"
                              >
                                Open full history <ExternalLink className="h-3 w-3" aria-hidden />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>

      {/* Pinned fleet-summary footer — same structure/height as NetworkDashboardGrid's. */}
      <div className="flex shrink-0 items-center gap-3 border-t border-gray-100 px-5 py-3"
           data-testid="room-landing-footer">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-600">
          <span className="font-medium text-gray-500" data-testid="rooms-fleet-count">
            {fleet.count} room{fleet.count === 1 ? '' : 's'}
          </span>
          <span className={`flex items-center gap-1 ${fleet.active > 0 ? 'text-emerald-600' : ''}`}
                title="Rooms running now">
            <Activity className="h-3.5 w-3.5" aria-hidden />
            <span className="tabular-nums" data-testid="rooms-fleet-active">{fleet.active}</span> active
          </span>
          {fleet.runsTotal > 0 ? (
            <span className="flex flex-col gap-0.5" title="Executions across the shown rooms"
                  data-testid="rooms-fleet-runs">
              <span className="flex items-center gap-2">
                <span className="flex items-center gap-0.5 text-emerald-600" title="Succeeded">
                  <Check className="h-3.5 w-3.5" aria-hidden />
                  <span className="tabular-nums" data-testid="rooms-fleet-succeeded">{fleet.runsSucceeded}</span>
                </span>
                <span className={`flex items-center gap-0.5 ${fleet.runsFailed > 0 ? 'text-red-600' : 'text-gray-300'}`}
                      title="Failed">
                  <X className="h-3.5 w-3.5" aria-hidden />
                  <span className="tabular-nums" data-testid="rooms-fleet-failed">{fleet.runsFailed}</span>
                </span>
                <span className="text-gray-400">/ <span className="tabular-nums" data-testid="rooms-fleet-runs-total">{fleet.runsTotal}</span></span>
                <span className={`font-semibold tabular-nums ${
                  fleet.successPct >= 90 ? 'text-emerald-600'
                    : fleet.successPct >= 70 ? 'text-amber-600' : 'text-red-600'
                }`} data-testid="rooms-fleet-success-pct">{fleet.successPct}%</span>
              </span>
              <span className="flex h-1.5 w-full min-w-[80px] overflow-hidden rounded-full bg-gray-200"
                    data-testid="rooms-fleet-runs-bar" aria-hidden>
                <span className="h-full bg-emerald-500" style={{ width: `${fleet.successPct}%` }} />
                <span className="h-full bg-red-500" style={{ width: `${100 - fleet.successPct}%` }} />
              </span>
            </span>
          ) : (
            <span className="text-gray-400" data-testid="rooms-fleet-runs">no executions</span>
          )}
          <span className="flex items-center gap-1"
                title={footerUnit === 'usd' ? 'Estimated cost consumed' : 'Points consumed'}
                data-testid="rooms-fleet-consumption">
            <Server className="h-3.5 w-3.5 shrink-0 text-gray-500" aria-hidden />
            <ConsumptionMeter unit={footerUnit} consumed={fleet.points} consumedUsd={fleet.usd} budget={0} hideUnit />
            <select
              value={footerUnit}
              onChange={(e) => setFooterUnit(e.target.value)}
              aria-label="Consumption unit"
              title="Show cost in $ or points"
              data-testid="rooms-fleet-unit"
              className="shrink-0 cursor-pointer rounded border-0 bg-transparent py-0 pl-0.5 pr-3 text-[10px] text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-0"
            >
              <option value="usd">$</option>
              <option value="points">pts</option>
            </select>
          </span>
        </div>

        {maxPage > 0 ? (
          <div className="flex shrink-0 items-center gap-2 text-[11px] text-gray-500">
            <button type="button" disabled={safePage <= 0} onClick={goPrev}
              data-testid="room-landing-prev"
              className="flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 hover:bg-gray-50 disabled:opacity-40">
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </button>
            <span className="tabular-nums">Page {safePage + 1} / {maxPage + 1}</span>
            <button type="button" disabled={safePage >= maxPage} onClick={goNext}
              data-testid="room-landing-next"
              className="flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 hover:bg-gray-50 disabled:opacity-40">
              Next <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
